import * as Sentry from '@sentry/vue'

// The schemes the app can take over: `web+todo:` links, and `geo:` ones, which it sends on to the user's map.
export const PROTOCOL_SCHEMES = ['web+todo', 'geo'] as const
export type ProtocolScheme = (typeof PROTOCOL_SCHEMES)[number]

// `unsupported`: the browser (or the Telegram webview) has no such API; `blocked`: it refused (SecurityError, e.g. not https).
export type RegisterResult = 'ok' | 'unsupported' | 'blocked' | 'error'

export function isSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.registerProtocolHandler === 'function'
}

// The page the browser sends a link to: `%s` becomes the whole escaped link, `/open` (`pages/OpenLinkPage.vue`) works it out.
export function handlerTemplate(): string {
  return `${location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}/open?uri=%s`
}

function report(error: unknown, scheme: ProtocolScheme, action: string): RegisterResult {
  if (error instanceof DOMException && error.name === 'SecurityError') return 'blocked'
  Sentry.captureException(error, { tags: { protocol_scheme: scheme, protocol_action: action } })
  return 'error'
}

// Has to be called from a tap: browsers ignore (or never show the prompt for) a registration without a user gesture.
export function registerHandler(scheme: ProtocolScheme): RegisterResult {
  if (!isSupported()) return 'unsupported'
  try {
    navigator.registerProtocolHandler(scheme, handlerTemplate())
    return 'ok'
  } catch (error) {
    return report(error, scheme, 'register')
  }
}

// Not every browser has it (unverified which), so the button is only shown where the method exists.
export function canUnregister(): boolean {
  return (
    typeof (navigator as { unregisterProtocolHandler?: unknown }).unregisterProtocolHandler ===
    'function'
  )
}

export function unregisterHandler(scheme: ProtocolScheme): RegisterResult {
  if (!canUnregister()) return 'unsupported'
  try {
    ;(
      navigator as unknown as { unregisterProtocolHandler(scheme: string, url: string): void }
    ).unregisterProtocolHandler(scheme, handlerTemplate())
    return 'ok'
  } catch (error) {
    return report(error, scheme, 'unregister')
  }
}
